import { useId, useState, type FormEvent } from 'react'
import { Alert, ApiError, Button, Field } from '@jmvilaca-corp/auth-ui'
import { fieldErrors } from '../lib/calendars'
import { useLinkConnector, type CalendarSource } from '../lib/connectors'
import Modal from './Modal'

/** Identification aupres d'une autre application, pour y lier un calendrier. */
export default function ConnectorSignInDialog({
  source,
  onLinked,
  onClose,
}: {
  source: Pick<CalendarSource, 'key' | 'label' | 'login'>
  onLinked: () => void
  onClose: () => void
}) {
  const link = useLinkConnector(source.key)
  const [login, setLogin] = useState(source.login ?? '')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [failure, setFailure] = useState<string | null>(null)
  const formId = useId()

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    setFailure(null)

    const local: Record<string, string> = {}
    if (login.trim().length === 0) {
      local.login = "L'identifiant est obligatoire."
    }
    if (password.length === 0) {
      local.password = 'Le mot de passe est obligatoire.'
    }

    setErrors(local)
    if (Object.keys(local).length > 0) {
      return
    }

    link.mutate(
      { login: login.trim(), password },
      {
        onSuccess: onLinked,
        onError: (error) => {
          const byField = error instanceof ApiError ? fieldErrors(error.payload) : {}
          setErrors(byField)
          setFailure(Object.keys(byField).length > 0 ? null : error.message)
        },
      },
    )
  }

  return (
    <Modal
      title={`Connexion a ${source.label}`}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" form={formId} size="sm" disabled={link.isPending}>
            {link.isPending ? 'Verification...' : 'Se connecter'}
          </Button>
        </div>
      }
    >
      <form id={formId} onSubmit={onSubmit} noValidate className="space-y-4">
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          {source.login === null
            ? `Pour lier un calendrier a ${source.label}, nous avons besoin que vous vous identifiiez une premiere fois.`
            : `${source.label} refuse les identifiants enregistres. Saisissez-les a nouveau.`}{' '}
          Vos identifiants sont verifies aupres de {source.label}, puis enregistres chiffres : ils serviront a
          importer votre calendrier.
        </p>

        {failure !== null && <Alert>{failure}</Alert>}

        <div>
          <Field
            label={`Adresse e-mail ${source.label}`}
            type="email"
            autoComplete="off"
            value={login}
            autoFocus
            onChange={(event) => setLogin(event.target.value)}
          />
          {errors.login !== undefined && <FieldError>{errors.login}</FieldError>}
        </div>

        <div>
          <Field
            label={`Mot de passe ${source.label}`}
            type="password"
            // Pas le mot de passe de cette application : le navigateur ne doit ni le proposer ni l'ecraser.
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          {errors.password !== undefined && <FieldError>{errors.password}</FieldError>}
        </div>

      </form>
    </Modal>
  )
}

function FieldError({ children }: { children: string }) {
  return <p className="mt-1.5 text-sm text-red-700 dark:text-red-300">{children}</p>
}
